import { useEffect, useState } from "react"
import { usePage } from "@inertiajs/react"
import { toast } from "sonner"
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { csrfToken } from "@/lib/csrf"
import { track } from "@/lib/analytics"
import type { SharedProps } from "@/types"

const STORAGE_KEY = "qr_intro_offer_dismissed_date"

function dismissedToday(): boolean {
  if (typeof window === "undefined") return false
  return localStorage.getItem(STORAGE_KEY) === new Date().toDateString()
}

export default function IntroOfferModal() {
  const { introOffer } = usePage<SharedProps>().props
  const [open, setOpen] = useState(() => !!introOffer && !dismissedToday())
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (open && introOffer) {
      track("intro_offer_shown", { surface: "storyboard_modal", plan_id: introOffer.plan_id })
    }
    // Only track once on initial mount, when the modal auto-opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function handleClaim() {
    if (!introOffer) return
    setLoading(true)
    try {
      const res = await fetch("/dashboard/checkout_sessions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({ plan_id: introOffer.plan_id }),
      })
      const { url, error } = (await res.json()) as { url?: string; error?: string }
      if (url) {
        track("intro_offer_claimed", { surface: "storyboard_modal", plan_id: introOffer.plan_id })
        window.location.href = url
      } else {
        toast.error(error ?? "Something went wrong")
        setLoading(false)
      }
    } catch {
      toast.error("Something went wrong")
      setLoading(false)
    }
  }

  function handleNoThanks() {
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, new Date().toDateString())
    }
    if (introOffer) {
      track("intro_offer_dismissed", { surface: "storyboard_modal", plan_id: introOffer.plan_id })
    }
    setOpen(false)
  }

  if (!introOffer) return null

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : setOpen(false))}>
      <DialogContent
        className="overflow-hidden border-0 p-0 sm:max-w-2xl"
        showCloseButton={false}
      >
        {/* Hero band */}
        <div className="relative bg-gradient-to-br from-charcoal via-charcoal to-red px-8 py-9 text-white">
          <div className="pointer-events-none absolute -right-12 -top-12 size-48 rounded-full bg-white/[0.06] blur-2xl" />
          <div className="pointer-events-none absolute -bottom-14 -left-12 size-56 rounded-full bg-red/30 blur-3xl" />
          <p className="relative text-[11px] font-semibold uppercase tracking-[0.22em] text-white/60">
            Welcome offer · new agents only
          </p>
          <DialogTitle asChild>
            <h2 className="relative mt-3 font-display text-3xl font-semibold leading-tight sm:text-[34px]">
              Your first {introOffer.video_count} videos for {introOffer.price_label}
            </h2>
          </DialogTitle>
          <DialogDescription asChild>
            <p className="relative mt-3 max-w-xl text-[15px] leading-relaxed text-white/80">
              {introOffer.credits.toLocaleString()} credits — enough for{" "}
              {introOffer.video_count} polished property videos. One-off, no
              subscription.
            </p>
          </DialogDescription>
        </div>

        {/* Body */}
        <div className="space-y-6 px-8 py-7">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-stone/40 bg-cream/40 p-4">
              <div className="flex items-center gap-2">
                <span className="flex size-6 items-center justify-center rounded-full bg-red/10 text-[11px] font-semibold text-red">
                  1
                </span>
                <p className="text-xs font-semibold uppercase tracking-wider text-charcoal">
                  Do it yourself
                </p>
              </div>
              <p className="mt-2 text-sm leading-snug text-warm-grey">
                Build five videos using the storyboard — finished in minutes
                per listing.
              </p>
            </div>
            <div className="rounded-lg border border-stone/40 bg-cream/40 p-4">
              <div className="flex items-center gap-2">
                <span className="flex size-6 items-center justify-center rounded-full bg-red/10 text-[11px] font-semibold text-red">
                  2
                </span>
                <p className="text-xs font-semibold uppercase tracking-wider text-charcoal">
                  Or, we'll do it
                </p>
              </div>
              <p className="mt-2 text-sm leading-snug text-warm-grey">
                Email{" "}
                <a
                  href="mailto:team@quiteright.co.uk?subject=Make%20my%20first%205%20videos"
                  className="font-medium text-red hover:text-deep-red"
                >
                  team@quiteright.co.uk
                </a>{" "}
                and we'll produce them for you.
              </p>
            </div>
          </div>

          <Button
            onClick={handleClaim}
            disabled={loading}
            className="h-12 w-full bg-red text-base font-semibold text-white shadow-sm hover:bg-red/90"
          >
            {loading ? "Loading…" : `Claim the ${introOffer.price_label} offer`}
          </Button>

          <button
            onClick={handleNoThanks}
            className="block w-full text-center text-xs text-warm-grey transition-colors hover:text-charcoal"
          >
            No thanks
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
